import { create } from 'zustand';

/**
 * Which cards are selected on the board, and in which column (spec 098 FR-005
 * – FR-007). In memory only: a selection is a moment's intent, never a view to
 * share or restore.
 *
 * One column at a time, by rule: bulk actions are defined by the step the cards
 * are on, so a selection across steps would have no single answer. `toggle`
 * says `'conflict'` rather than deciding; the board asks the reader.
 */
export interface BoardSelectionState {
  workspaceId: string | null;
  stepId: string | null;
  taskIds: string[];
  toggle: (
    workspaceId: string,
    stepId: string,
    taskId: string,
  ) => 'toggled' | 'conflict';
  replaceWith: (workspaceId: string, stepId: string, taskId: string) => void;
  /** Drop ids no longer visible in the step; clear when none remain. */
  prune: (
    workspaceId: string,
    stepId: string,
    visibleIds: readonly string[],
  ) => void;
  remove: (taskIds: readonly string[]) => void;
  clear: () => void;
  /**
   * Select mode (spec 262): every card offers its box and a tap ticks it,
   * with nothing ticked yet. A phone's way in, where there is no Shift and no
   * hover to show the box. Ends with Done, or with a cleared selection.
   */
  picking: boolean;
  setPicking: (picking: boolean) => void;
}

const EMPTY = { workspaceId: null, stepId: null, taskIds: [] as string[] };

export const useBoardSelectionStore = create<BoardSelectionState>()(
  (set, get) => ({
    ...EMPTY,
    toggle: (workspaceId, stepId, taskId) => {
      const state = get();
      const sameColumn =
        state.taskIds.length === 0 ||
        (state.workspaceId === workspaceId && state.stepId === stepId);
      if (!sameColumn) return 'conflict';
      const has = state.taskIds.includes(taskId);
      const taskIds = has
        ? state.taskIds.filter((id) => id !== taskId)
        : [...state.taskIds, taskId];
      set(
        taskIds.length === 0 ? { ...EMPTY } : { workspaceId, stepId, taskIds },
      );
      return 'toggled';
    },
    replaceWith: (workspaceId, stepId, taskId) => {
      set({ workspaceId, stepId, taskIds: [taskId] });
    },
    prune: (workspaceId, stepId, visibleIds) => {
      const state = get();
      if (state.workspaceId !== workspaceId || state.stepId !== stepId) return;
      const kept = state.taskIds.filter((id) => visibleIds.includes(id));
      if (kept.length === state.taskIds.length) return;
      set(kept.length === 0 ? { ...EMPTY } : { taskIds: kept });
    },
    remove: (taskIds) => {
      const kept = get().taskIds.filter((id) => !taskIds.includes(id));
      set(kept.length === 0 ? { ...EMPTY } : { taskIds: kept });
    },
    clear: () => {
      set({ ...EMPTY, picking: false });
    },
    picking: false,
    setPicking: (picking) => {
      set(picking ? { picking } : { ...EMPTY, picking });
    },
  }),
);
